import axios from 'axios';
import { API_BASE_URL } from './apiConfig';

// Create axios instance with base configuration
const sessionApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to add token to headers
sessionApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for better error handling
sessionApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('sessionId');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Session Management API endpoints
const LoginAuthSessionApi = {
  // Check current session
  checkSession: async () => {
    try {
      const response = await sessionApi.get('/api/v2/auth/session');
      return response.data;
    } catch (error) {
      console.error('Session check API error:', error);
      throw error;
    }
  },

  // Refresh session/token
  refreshSession: async () => {
    try {
      const response = await sessionApi.post('/api/v2/auth/refresh');
      return response.data;
    } catch (error) {
      console.error('Refresh session API error:', error);
      throw error;
    }
  },

  // Get all user sessions
  getUserSessions: async () => {
    try {
      const response = await sessionApi.get('/api/v2/auth/sessions');
      return response.data;
    } catch (error) {
      console.error('Get user sessions API error:', error);
      throw error;
    }
  },

  // Delete a specific session
  deleteSession: async (sessionId) => {
    try {
      const response = await sessionApi.delete(`/api/v2/auth/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Delete session API error:', error);
      throw error;
    }
  },

  // Clean expired sessions (Admin only)
  cleanExpiredSessions: async () => {
    try {
      const response = await sessionApi.post('/api/v2/auth/admin/clean-sessions');
      return response.data;
    } catch (error) {
      console.error('Clean expired sessions API error:', error);
      throw error;
    }
  },

  // Activity logging
  logActivity: async (activityType, details = {}) => {
    try {
      const sessionId = localStorage.getItem('sessionId');
      const payload = {
        type: activityType,
        sessionId,
        timestamp: new Date().toISOString(),
        details: {
          ...details,
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language,
          screenResolution: `${window.screen.width}x${window.screen.height}`
        }
      };

      const response = await sessionApi.post('/api/v2/activity/log', payload);
      return response.data;
    } catch (error) {
      console.error('Activity logging API error:', error);
      // Don't throw - activity logging should not break the app
      return { success: false, error: error.message };
    }
  },

  // Get activity logs
  getActivityLogs: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const response = await sessionApi.get(`/api/v2/activity/logs?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Get activity logs API error:', error);
      throw error;
    }
  },

  // Session heartbeat (keep-alive)
  sessionHeartbeat: async () => {
    try {
      const response = await sessionApi.post('/api/v2/auth/heartbeat');
      return response.data;
    } catch (error) {
      console.error('Session heartbeat API error:', error);
      // Don't throw - heartbeat should not break the app
      return { success: false, error: error.message };
    }
  },

  // Validate session token
  validateToken: async (token) => {
    try {
      const response = await sessionApi.post('/api/v2/auth/validate-token', { token });
      return response.data;
    } catch (error) {
      console.error('Validate token API error:', error);
      throw error;
    }
  },

  // Get session info
  getSessionInfo: async () => {
    try {
      const response = await sessionApi.get('/api/v2/auth/session-info');
      return response.data;
    } catch (error) {
      console.error('Get session info API error:', error);
      throw error;
    }
  },

  // Extend session
  extendSession: async () => {
    try {
      const response = await sessionApi.post('/api/v2/auth/extend-session');
      return response.data;
    } catch (error) {
      console.error('Extend session API error:', error);
      throw error;
    }
  },

  // Get active sessions count
  getActiveSessionsCount: async () => {
    try {
      const response = await sessionApi.get('/api/v2/auth/active-sessions-count');
      return response.data;
    } catch (error) {
      console.error('Get active sessions count API error:', error);
      throw error;
    }
  },

  // Session security check
  performSecurityCheck: async () => {
    try {
      const response = await sessionApi.post('/api/v2/auth/security-check', {
        timestamp: new Date().toISOString(),
        fingerprint: await generateFingerprint()
      });
      return response.data;
    } catch (error) {
      console.error('Security check API error:', error);
      throw error;
    }
  },

  // Set session metadata
  setSessionMetadata: async (metadata) => {
    try {
      const response = await sessionApi.post('/api/v2/auth/session-metadata', metadata);
      return response.data;
    } catch (error) {
      console.error('Set session metadata API error:', error);
      throw error;
    }
  },

  // Get axios instance (for custom requests)
  getAxiosInstance: () => sessionApi
};

// Helper function to generate browser fingerprint
const generateFingerprint = async () => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillText('fingerprint', 2, 2);
  const dataURL = canvas.toDataURL();
  
  const fingerprint = {
    canvas: dataURL.substring(0, 50), // Just a sample, not full canvas
    screen: `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`,
    timezone: new Date().getTimezoneOffset(),
    language: navigator.language,
    platform: navigator.platform,
    cookieEnabled: navigator.cookieEnabled,
    plugins: Array.from(navigator.plugins || []).map(p => p.name).join(','),
    userAgent: navigator.userAgent
  };
  
  // Simple hash of the fingerprint object
  const str = JSON.stringify(fingerprint);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  return hash.toString(36);
};

export default LoginAuthSessionApi;
